import { useLanguage, useLanguages } from '@api/application-configuration';
import { Check, Globe2 } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Image } from '@unistocks/components/basic/image';
import { Menu } from '@unistocks/components/menu/menu';
import { FLAGS, langToFlag } from '@unistocks/features/portfolios/flags';
import { t } from '@unistocks/localization';
import { abpApplicationConfigurationGet } from '@unistocks/proxy/axios-client/AbpApplicationConfigurationClient';
import { ACCEPT_LANGUAGE_HEADER } from '@unistocks/shared/consts';
import { APP_CONFIG_QUERY_KEY } from '@unistocks/shared/hooks/use-application-configuration';
import { useState } from 'react';
import { Button, Spinner } from 'tamagui';

/**
 * Simple language switch component that allows users to change the application's language
 * without saving to the user's profile settings.
 */
export const LanguageSwitch: React.FC = () => {
  const queryClient = useQueryClient();
  const languages = useLanguages();
  const activeLanguage = useLanguage();
  const [open, setOpen] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState<string>();

  const setActiveLanguage = async (cultureName: string): Promise<void> => {
    setPendingLanguage(cultureName);

    try {
      await queryClient.query({
        queryKey: APP_CONFIG_QUERY_KEY,
        queryFn: () =>
          abpApplicationConfigurationGet(false, {
            headers: { [ACCEPT_LANGUAGE_HEADER]: cultureName }
          }),
        staleTime: 0
      });

      setOpen(false);
    } finally {
      setPendingLanguage(undefined);
    }
  };

  const items =
    languages?.map(lang => ({
      key: lang.displayName,
      value: lang.cultureName,
      icon: (
        <Image
          rounded
          size={'$1.5'}
          source={FLAGS[langToFlag[lang.twoLetterISOLanguageName]]}
          alt={`${lang.displayName} flag`}
        />
      )
    })) ?? [];

  const selectedItem = languages?.find(l => l.cultureName === activeLanguage);

  return (
    <Menu
      open={open}
      onOpenChange={setOpen}
      trigger={
        <Button unstyled cursor="pointer" alignItems="center" justifyContent="center">
          {selectedItem ? (
            <Image
              rounded
              size={'$1.5'}
              source={FLAGS[langToFlag[selectedItem.twoLetterISOLanguageName]]}
              alt={`${selectedItem.displayName} flag`}
            />
          ) : (
            <Globe2 size={'$1'} color={'$color'} />
          )}
        </Button>
      }
      headerTitle={t('::SelectWhat', { 0: t('::Language').toLowerCase() })}
      headerIcon={<Globe2 color={'$success'} />}
    >
      {items.map(item => {
        const isSelected = item.value === activeLanguage;
        const isSaving = item.value === pendingLanguage;

        return (
          <Menu.Item
            key={item.key}
            closeOnPress={false}
            disabled={!!pendingLanguage}
            icon={item.icon}
            iconAfter={
              isSaving ? (
                <Spinner color={'$color'} size={'small'} />
              ) : isSelected ? (
                <Check color={'$success'} />
              ) : undefined
            }
            title={item.key}
            onPress={() => setActiveLanguage(item.value)}
          />
        );
      })}
    </Menu>
  );
};

LanguageSwitch.displayName = 'LanguageSwitch';
